import PROFILE_TYPE from '../../../../constants/profileTypes';

const REQUIRED_PERMISSIONS_BY_ROUTE = {
    basic: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantProfile.Edit',
            'Permission.ApplicantProfile.View',
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentProfile.Edit',
            'Permission.StudentProfile.View',
        ],
        [PROFILE_TYPE.ENQUIRY]: [
            'Permission.EnquiryProfile.View',
        ]
    },
    contact: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantContacts.Edit',
            'Permission.ApplicantContacts.View',
            'Permission.ApplicantEthnicity.Edit',
            'Permission.ApplicantEthnicity.View',
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentContacts.Edit',
            'Permission.StudentContacts.View',
            'Permission.StudentEthnicity.Edit',
            'Permission.StudentEthnicity.View',
        ],
        [PROFILE_TYPE.ENQUIRY]: [
            'Permission.EnquiryContacts.View',
        ]
    },
    sen: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantSEN.Edit',
            'Permission.ApplicantSEN.View',
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentSEN.Edit',
            'Permission.StudentSEN.View',
        ]
    },
    ethnicity: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantDietaryNeedsAndFoodAllergies.Edit',
            'Permission.ApplicantDietaryNeedsAndFoodAllergies.View',
            'Permission.ApplicantFreeSchoolMealsEligibility.Edit',
            'Permission.ApplicantFreeSchoolMealsEligibility.View',
            'Permission.ApplicantWelfare.Edit',
            'Permission.ApplicantWelfare.View',
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentDietaryNeedsAndFoodAllergies.Edit',
            'Permission.StudentDietaryNeedsAndFoodAllergies.View',
            'Permission.StudentFreeSchoolMealsEligibility.Edit',
            'Permission.StudentFreeSchoolMealsEligibility.View',
            'Permission.StudentWelfare.Edit',
            'Permission.StudentWelfare.View',
        ]
    },
    consents: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantConsents.View',
            'Permission.ApplicantConsents.Edit',
            'Permission.ApplicantTravelMode.View',
            'Permission.ApplicantTravelMode.Edit'
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentConsents.View',
            'Permission.StudentConsents.Edit',
            'Permission.StudentTravelMode.View',
            'Permission.StudentTravelMode.Edit'
        ]
    },
    medical: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantMedicalConditions.View',
            'Permission.ApplicantMedicalConditions.Edit',
            'Permission.ApplicantMedicalEvents.View',
            'Permission.ApplicantMedicalEvents.Edit',
            'Permission.ApplicantMedicalNotes.View',
            'Permission.ApplicantMedicalNotes.Edit',
            'Permission.ApplicantGpInfo.View',
            'Permission.ApplicantGpInfo.Edit',
            'Permission.ApplicantMedicalConsents.View',
            'Permission.ApplicantMedicalConsents.Edit'
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentMedicalConditions.View',
            'Permission.StudentMedicalConditions.Edit',
            'Permission.StudentMedicalEvents.View',
            'Permission.StudentMedicalEvents.Edit',
            'Permission.StudentMedicalNotes.View',
            'Permission.StudentMedicalNotes.Edit',
            'Permission.StudentGpInfo.View',
            'Permission.StudentGpInfo.Edit',
            'Permission.StudentMedicalConsents.View',
            'Permission.StudentMedicalConsents.Edit',
        ]
    },
    groups: {
        [PROFILE_TYPE.APPLICATION]: [
            'Permission.ApplicantProfile.Edit',
            'Permission.ApplicantProfile.View',
        ],
        [PROFILE_TYPE.STUDENT]: [
            'Permission.StudentProfile.Edit',
            'Permission.StudentProfile.View',
        ]
    }
};

const shouldRender = (userPermissions, parentPermissions, requiredPermissions, personType, isParentOfStudent = false) => {
    if (!Object.values(PROFILE_TYPE).filter(x => x).includes(personType)) {
        return false;
    }

    const permissions = requiredPermissions[personType];

    if (!permissions) {
        return false;
    }

    // Check general permissions first
    const hasGeneralPermission = (Array.isArray(permissions) && permissions.some(x => userPermissions.includes(x))) || permissions === '*';

    if (hasGeneralPermission) {
        return true;
    }

    // If user is parent of student, check parent permissions
    if (isParentOfStudent && parentPermissions && parentPermissions.length > 0) {
        return (Array.isArray(permissions) && permissions.some(x => parentPermissions.includes(x))) || permissions === '*';
    }

    return false;
};

export const tabs = [
    {
        label: 'Basic',
        route: 'basic',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.basic,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: true
    },
    {
        label: (personType) => personType === PROFILE_TYPE.ENQUIRY
            ? 'Contacts'
            : 'Contacts and Cultural Information',
        route: 'contact',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.contact,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: true
    },
    {
        label: 'Medical',
        route: 'medical',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.medical,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: true
    },
    {
        label: 'SEN and Disabilities',
        route: 'sen',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.sen,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: true
    },
    {
        label: 'Dietary and Welfare',
        route: 'ethnicity',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.ethnicity,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: true
    },
    {
        label: 'Consents and Transport',
        route: 'consents',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.consents,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: true
    },
    {
        label: 'Documentation',
        route: 'documentation',
        shouldRender: (_) => true,
        showLoadingSpinner: true
    },
    {
        label: 'Forms',
        route: 'forms',
        shouldRender: (_) => true,
        showLoadingSpinner: false
    },
    {
        label: 'Groups',
        route: 'groups',
        shouldRender: (permissions, parentPermissions, personType, isParentOfStudent) => shouldRender(
            permissions,
            parentPermissions,
            REQUIRED_PERMISSIONS_BY_ROUTE.groups,
            personType,
            isParentOfStudent
        ),
        showLoadingSpinner: false
    }
];